import dayjs from "dayjs";
import { extractProcessAssociationIdFromListItem } from "../utils/enrichManualWorkflowProcessAssociation";
import {
  parseTaskOwnerSelectionOptions,
  resolveManualWorkflowAttachmentS3Uri,
  resolveManualWorkflowPreviousFormsUri,
  type FreyrRecipientOption,
  type ManualAssessmentReportShareRequest,
} from "../utils/manualAssessmentReportShare";
import { apiClient, type ApiResponse } from "./apiClient";

export type ManualWorkflowApiItem = {
  id: number;
  uuid: string;
  regulation_id: string;
  regulation_version: string;
  regulation_title: string | null;
  process_instance_id: string | null;
  process_name: string | null;
  /** Plan & Track process association id for `/process/{id}` links */
  process_association_id?: string | null;
  processAssociationId?: string | null;
  status: string;
  initiated_by: string | null;
  initiated_at: string | null;
  attachmentS3Uri?: string | null;
  attachment_s3_uri?: string | null;
  reportS3Uri?: string | null;
  report_s3_uri?: string | null;
  report_url?: string | null;
  reportUrl?: string | null;
  references?: Array<{
    uri?: string | null;
    name?: string | null;
    content_type?: string | null;
    field?: string | null;
    label?: string | null;
  }> | null;
};

export type ManualWorkflowsListResult = {
  items: ManualWorkflowApiItem[];
  total: number;
  page: number;
  pageSize: number;
};

export type GetManualWorkflowsParams = {
  page?: number;
  page_size?: number;
  regulation_id?: string;
  regulation_version?: string;
  status?: string;
  initiated_date?: string;
  process_name?: string;
  initiated_by?: string;
  /**
   * Backend expects a single sort field, e.g. "-initiated_date" / "initiated_date",
   * "-process_name" / "process_name", etc.
   */
  sort?: string;
};

/** One entry from MUI `GridSortModel` — kept minimal to avoid coupling services to MUI types. */
export type ManualWorkflowListSortItem = {
  field: string;
  sort?: "asc" | "desc" | null;
};

const MANUAL_WORKFLOW_SORT_FIELD_TO_API: Record<string, string> = {
  regulationId: "regulation_id",
  regulation_id: "regulation_id",
  version: "regulation_version",
  regulation_version: "regulation_version",
  processName: "process_name",
  process_name: "process_name",
  initiatedBy: "initiated_by",
  initiated_by: "initiated_by",
  initiatedDate: "initiated_date",
  initiated_date: "initiated_date",
  status: "status",
};

/**
 * Maps Assessment Center manual grid sort model → `GET /manual-assessment/workflows` `sort` query.
 * Trims field / token parts so stray whitespace never reaches the API.
 */
export function manualWorkflowListSortParamFromSortModel(
  sortModel: readonly ManualWorkflowListSortItem[],
): string {
  const first = sortModel[0];
  if (!first?.sort) return "-initiated_date";
  const field = (first.field ?? "").trim();
  const sortDir = String(first.sort).trim();
  const apiField = (
    MANUAL_WORKFLOW_SORT_FIELD_TO_API[field] ?? "initiated_date"
  ).trim();
  return sortDir === "desc" ? `-${apiField}` : apiField;
}

/**
 * DELETE /manual-assessment/workflows — terminate workflows by numeric workflow ids.
 */
export async function terminateManualAssessmentWorkflows(
  workflowIds: number[],
): Promise<{ status?: string; message?: string }> {
  return apiClient.delete("/manual-assessment/workflows", {
    data: { workflowIds },
  });
}

/**
 * POST /manual-assessment/report-shares
 * Sends the generated Manual IA PDF / assessment link to Freyr users, groups, or roles.
 */
export async function shareManualAssessmentReport(
  payload: ManualAssessmentReportShareRequest,
): Promise<ApiResponse<unknown>> {
  return apiClient.post("/manual-assessment/report-shares", payload);
}

/**
 * POST /manual-assessment/reports/user-presigned-url
 * View uses disposition `inline`; Download uses `attachment`.
 */
export type ManualAssessmentReportPresignedUrl = {
  presigned_url: string;
  filename?: string;
};

export type ManualAssessmentReportPresignedDisposition = "inline" | "attachment";

export async function getManualAssessmentReportPresignedUrl(
  filePath: string,
  disposition: ManualAssessmentReportPresignedDisposition,
): Promise<ManualAssessmentReportPresignedUrl> {
  const response = await apiClient.post<ManualAssessmentReportPresignedUrl>(
    "/manual-assessment/reports/user-presigned-url",
    {
      file_path: filePath,
      disposition,
    },
  );

  if (response.status === "success" && response.data?.presigned_url) {
    return response.data;
  }

  throw new Error(response.message || "Failed to get presigned URL");
}

/**
 * GET /manual-assessment/report-share-recipients
 * Same grouped shape as Triage "Notify Additional Recipients / Group":
 * `{ taskUsers, taskGroups, taskRoles }` items with `id` / `name` / `email`.
 */
export async function getManualAssessmentReportShareRecipients(): Promise<
  FreyrRecipientOption[]
> {
  const result = await apiClient.get<unknown>(
    "/manual-assessment/report-share-recipients",
  );
  return parseTaskOwnerSelectionOptions(result);
}

export type SyncManualAssessmentWorkflowStatusParams = {
  regulation_id: string;
  regulation_version: string;
};

/** Normalize regulation scope for POST /manual-assessment/workflows/status. */
export function buildSyncManualAssessmentWorkflowStatusParams(
  regulationId: string | null | undefined,
  regulationVersion: string | null | undefined,
): SyncManualAssessmentWorkflowStatusParams | null {
  const id = (regulationId ?? "").trim();
  const rawVersion = (regulationVersion ?? "").trim();
  if (!id || !rawVersion) return null;

  const version = rawVersion.startsWith("v")
    ? rawVersion.slice(1).trim()
    : rawVersion;
  if (!version) return null;

  return { regulation_id: id, regulation_version: version };
}

/**
 * POST /manual-assessment/workflows/status
 * Syncs workflow status (and process name) from Plan & Track for one regulation.
 * Both `regulation_id` and `regulation_version` are required by the backend.
 */
export async function syncManualAssessmentWorkflowStatus(
  params: SyncManualAssessmentWorkflowStatusParams,
): Promise<void> {
  const normalized = buildSyncManualAssessmentWorkflowStatusParams(
    params.regulation_id,
    params.regulation_version,
  );
  if (!normalized) return;

  await apiClient.post("/manual-assessment/workflows/status", {
    associatedEntityId: normalized.regulation_id,
    associatedEntityVersion: normalized.regulation_version,
  });
}

/** Status values for which the Assessment Center manual table may offer Terminate. */
export function isManualWorkflowTerminateEligibleStatus(status: string): boolean {
  const s = (status || "").trim().toLowerCase();
  return (
    s === "not started" ||
    s === "init" ||
    s === "in progress" ||
    s === "running"
  );
}

export async function getManualAssessmentWorkflows(
  params: GetManualWorkflowsParams = {},
): Promise<ManualWorkflowsListResult> {
  const page = params.page ?? 1;
  const page_size = params.page_size ?? 20;
  const sort =
    typeof params.sort === "string"
      ? params.sort.trim() || undefined
      : params.sort;

  const result = await apiClient.get<{
    items?: ManualWorkflowApiItem[];
    total?: number;
    page?: number;
    pageSize?: number;
  }>("/manual-assessment/workflows", {
    ...params,
    page,
    page_size,
    sort,
  });

  const payload = result.data ?? {};
  const items = Array.isArray(payload.items) ? payload.items : [];
  const total = typeof payload.total === "number" ? payload.total : items.length;
  const pageNum = typeof payload.page === "number" ? payload.page : page;
  const pageSize =
    typeof payload.pageSize === "number" ? payload.pageSize : page_size;

  return { items: items as ManualWorkflowApiItem[], total, page: pageNum, pageSize };
}

export type ManualWorkflowFilterOption = {
  name: string;
  value?: string | string[];
};

export type ManualWorkflowFilterConfig = {
  name: string;
  options: ManualWorkflowFilterOption[];
};

export type GetManualWorkflowFilterOptionsParams = {
  regulation_id?: string;
  regulation_version?: string;
};

/**
 * GET /manual-assessment/workflow-filter-options
 * Optional `regulation_id` / `regulation_version` when scoping options to a regulation.
 * Assessment Center manual tab calls this without regulation params.
 */
export async function getManualWorkflowFilterOptions(
  params: GetManualWorkflowFilterOptionsParams = {},
): Promise<ManualWorkflowFilterConfig[]> {
  const query: Record<string, string> = {};
  const rid = params.regulation_id?.trim();
  const rv = params.regulation_version?.trim();
  if (rid) query.regulation_id = rid;
  if (rv) query.regulation_version = rv;

  const result = await apiClient.get<ManualWorkflowFilterConfig[]>(
    "/manual-assessment/workflow-filter-options",
    Object.keys(query).length > 0 ? query : undefined,
  );
  return Array.isArray(result.data) ? result.data : [];
}

export type ManualImpactAssessmentGridRow = {
  id: string;
  /** Workflow uuid for GET /manual-assessment/workflows/{id} (PAT link on click). */
  workflowUuid: string;
  /** Numeric id required by DELETE /manual-assessment/workflows (workflowIds). */
  workflowDbId: number;
  regulationId: string;
  regulationTitle: string;
  version: string;
  processName: string;
  /** PAT link target when list includes it; otherwise resolved on Process name click. */
  processAssociationId: string;
  initiatedBy: string;
  initiatedDate: string;
  status: string;
  /** Generated Manual IA PDF locator when the backend has produced the report. */
  attachmentS3Uri: string;
  /** Plan & Track process URL from `references[].name === previousForms`. */
  assessmentLink: string;
};

export function mapManualWorkflowToGridRow(
  item: ManualWorkflowApiItem,
): ManualImpactAssessmentGridRow {
  const initiated = item.initiated_at
    ? dayjs(item.initiated_at).format("DD-MMM-YYYY")
    : "—";

  const processAssociationId = extractProcessAssociationIdFromListItem(
    item as Record<string, unknown>,
  );

  const workflowUuid = item.uuid?.trim() || "";

  return {
    id: workflowUuid || String(item.id),
    workflowUuid,
    workflowDbId: item.id,
    regulationId: item.regulation_id,
    regulationTitle: item.regulation_title?.trim() || item.regulation_id || "—",
    version: item.regulation_version,
    processName: item.process_name?.trim() || "—",
    processAssociationId,
    initiatedBy: item.initiated_by?.trim() || "—",
    initiatedDate: initiated,
    status: item.status || "—",
    attachmentS3Uri: resolveManualWorkflowAttachmentS3Uri(
      item as Record<string, unknown>,
    ),
    assessmentLink: resolveManualWorkflowPreviousFormsUri(
      item as Record<string, unknown>,
    ),
  };
}

/** Matches IA history / assessment-center `impacted_items` shape for triage recommendation. */
export type TriageImpactedItem = {
  type: string;
  keyword?: string | null;
  concept?: {
    name: string;
    code: string;
    description?: string | null;
  } | null;
  concept_value?: {
    name: string;
    code: string;
    description?: string | null;
  } | null;
  impacted?: boolean;
  impacted_level?: string | null;
};

export type TriageProcessesRecommendationRequest = {
  impacted_items: TriageImpactedItem[];
};

/** Whether structured IA `impacted_items` exist for triage recommend (not display text alone). */
export function hasTriageImpactedItems(
  items: readonly TriageImpactedItem[] | null | undefined,
): boolean {
  return coerceToTriageImpactedItems(items).length > 0;
}

/**
 * Triage on open only when opened from AI Impact **and** the row has `impacted_items`.
 * Otherwise use process-templates (direct button, or AI row without impacted_items).
 */
export function shouldLoadManualAssessmentViaTriage(
  entrySource: "from_ai_impact" | "direct",
  impactedItems: readonly TriageImpactedItem[] | null | undefined,
): boolean {
  return entrySource === "from_ai_impact" && hasTriageImpactedItems(impactedItems);
}

/**
 * Normalizes IA `impacted_items` (history / Assessment Center / poll payloads) for triage API.
 */
export function coerceToTriageImpactedItems(items: unknown): TriageImpactedItem[] {
  if (!Array.isArray(items)) return [];

  const mapConcept = (c: unknown): TriageImpactedItem["concept"] => {
    if (!c || typeof c !== "object") return null;
    const o = c as Record<string, unknown>;
    const name = typeof o.name === "string" ? o.name : "";
    const code = typeof o.code === "string" ? o.code : "";
    if (!name && !code) return null;
    const description =
      o.description === null || o.description === undefined
        ? null
        : String(o.description);
    return { name, code, description };
  };

  const mapConceptValue = (c: unknown): TriageImpactedItem["concept_value"] => {
    if (!c || typeof c !== "object") return null;
    const o = c as Record<string, unknown>;
    const name = typeof o.name === "string" ? o.name : "";
    const code = typeof o.code === "string" ? o.code : "";
    if (!name && !code) return null;
    const description =
      o.description === null || o.description === undefined
        ? null
        : String(o.description);
    return { name, code, description };
  };

  const out: TriageImpactedItem[] = [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Record<string, unknown>;
    const type = typeof o.type === "string" ? o.type : "concept";
    const keyword =
      o.keyword === null || o.keyword === undefined ? null : String(o.keyword);
    out.push({
      type,
      keyword,
      concept: mapConcept(o.concept),
      concept_value: mapConceptValue(o.concept_value),
      impacted: typeof o.impacted === "boolean" ? o.impacted : undefined,
      impacted_level:
        o.impacted_level === null || o.impacted_level === undefined
          ? null
          : String(o.impacted_level),
    });
  }
  return out;
}

export type ManualProcessSummary = {
  id: number;
  name: string;
  description: string | null;
  /** Present on `GET process-templates`; triage may omit until backend aligns. */
  definition_id?: string;
};

export type ProcessTemplateInfo = {
  id: number;
  name: string;
  definition_id: string;
  description: string;
};

export type ProcessTemplateGroup = {
  process_template: ProcessTemplateInfo;
  sub_process_templates: ProcessTemplateInfo[];
};

export type RegulationAssessmentResultPayload = {
  regulation_id: string;
  regulation_version: string;
  impacted: boolean;
  impacted_level: string;
  report_url: string | null;
  impacted_items: TriageImpactedItem[];
};

export type StartManualAssessmentRegulationContext = {
  regulationId: string;
  regulationVersion: string;
  regulationTitle: string;
};

export type StartWorkflowRequest = {
  regulation_id: string;
  regulation_version: string;
  regulation_title: string;
  process_template: ProcessTemplateInfo;
  sub_process_templates: ProcessTemplateInfo[];
  concept_assessment_result: RegulationAssessmentResultPayload | null;
  tenant_id?: string;
  references?: Array<{
    field: string;
    name: string;
    content_type: string;
    value: string;
  }>;
  /** Optional pre-init task payloads when process requires initialization. */
  init_tasks?: Array<{
    taskDefinitionId: string;
    taskCustomAssignee?: {
      taskUsers?: string[];
      taskGroups?: string[];
      taskRoles?: string[];
    };
    customPropertyMap?: Record<string, unknown>;
  }>;
};

export const MANUAL_ASSESSMENT_INITIATE_SUCCESS_TOAST =
  'Process initiated successfully. Tasks have been created. You can view task pick status and current progress in "My Task".';

/** Longer toast duration so users can read the initiate-success guidance. */
export const MANUAL_ASSESSMENT_INITIATE_SUCCESS_TOAST_MS = 6000;

export const MANUAL_ASSESSMENT_NO_PROCESS_TEMPLATES_MESSAGE =
  "No processes have been created yet. Please contact the support team to create processes first.";

export function buildConceptAssessmentResult(
  regulationId: string,
  regulationVersion: string,
  impacted: boolean,
  impactedLevel: string,
  reportUrl: string | null | undefined,
  impactedItems: readonly TriageImpactedItem[],
): RegulationAssessmentResultPayload {
  return {
    regulation_id: regulationId,
    regulation_version: regulationVersion,
    impacted,
    impacted_level: (impactedLevel || "no").trim().toLowerCase() || "no",
    report_url: reportUrl ?? null,
    impacted_items: impactedItems.map((i) => ({ ...i })),
  };
}

function findProcessTemplateById(
  groups: readonly ProcessTemplateGroup[],
  id: number,
): ProcessTemplateInfo | undefined {
  for (const group of groups) {
    if (group.process_template.id === id) {
      return group.process_template;
    }
    const sub = group.sub_process_templates.find((s) => s.id === id);
    if (sub) return sub;
  }
  return undefined;
}

/** Map triage/UI summary → workflow template; catalog (process-templates) is authoritative for `definition_id`. */
export function resolveProcessTemplateInfo(
  summary: ManualProcessSummary,
  catalog: readonly ProcessTemplateGroup[],
): ProcessTemplateInfo | null {
  const fromCatalog = findProcessTemplateById(catalog, summary.id);
  if (fromCatalog) {
    return fromCatalog;
  }
  const definitionId = summary.definition_id?.trim();
  if (!definitionId) return null;
  return {
    id: summary.id,
    name: summary.name,
    definition_id: definitionId,
    description: summary.description ?? "",
  };
}

export type TriageMatchedConceptValue = {
  name: string;
  code: string;
  description?: string | null;
};

export type TriageMatchedConceptGroup = {
  concept: {
    name: string;
    code: string;
    description?: string | null;
  };
  values: TriageMatchedConceptValue[];
};

export type RecommendedSubProcess = ManualProcessSummary & {
  matched_concepts?: TriageMatchedConceptGroup[];
};

/** Impacted Concepts value order: Department → Product Type → Ingredient → Therapeutic Area. */
const TRIAGE_MATCHED_CONCEPT_DISPLAY_ORDER = [
  "Department",
  "ProductType",
  "IngredientName",
  "Ingredient",
  "TherapeuticArea",
] as const;

function triageMatchedConceptSortKey(conceptName: string): number {
  const normalized = conceptName.trim();
  const idx = TRIAGE_MATCHED_CONCEPT_DISPLAY_ORDER.indexOf(
    normalized as (typeof TRIAGE_MATCHED_CONCEPT_DISPLAY_ORDER)[number],
  );
  return idx === -1 ? TRIAGE_MATCHED_CONCEPT_DISPLAY_ORDER.length : idx;
}

/** Comma-separated value labels for triage `matched_concepts` (no type prefix). */
export function formatTriageMatchedConcepts(
  groups: readonly TriageMatchedConceptGroup[] | null | undefined,
): string {
  if (!groups?.length) return "—";

  const sorted = [...groups].sort((a, b) => {
    const aName = a.concept?.name?.trim() || a.concept?.code?.trim() || "";
    const bName = b.concept?.name?.trim() || b.concept?.code?.trim() || "";
    return triageMatchedConceptSortKey(aName) - triageMatchedConceptSortKey(bName);
  });

  const parts: string[] = [];
  for (const g of sorted) {
    for (const v of g.values ?? []) {
      const label = v.name?.trim() || v.code?.trim() || "";
      if (label) parts.push(label);
    }
  }
  return parts.length > 0 ? parts.join(", ") : "—";
}

export type TriageProcessesRecommendationData = {
  main_process: ManualProcessSummary;
  sub_processes: ManualProcessSummary[];
  recommended_sub_processes: RecommendedSubProcess[];
};

/**
 * POST /manual-assessment/triage-processes-recommendation
 * Uses impacted concepts/keywords from an IA run to suggest manual main process and subprocesses.
 */
export async function postTriageProcessesRecommendation(
  body: TriageProcessesRecommendationRequest,
): Promise<ApiResponse<TriageProcessesRecommendationData>> {
  const result = await apiClient.post<TriageProcessesRecommendationData>(
    "/manual-assessment/triage-processes-recommendation",
    body,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code ?? 200,
    message: result.message,
    data: result.data as TriageProcessesRecommendationData,
  };
}

function normalizeProcessTemplateInfo(raw: unknown): ProcessTemplateInfo | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = Number(o.id);
  if (!Number.isFinite(id)) return null;
  return {
    id,
    name: String(o.name ?? "").trim(),
    definition_id: String(o.definition_id ?? o.definitionId ?? "").trim(),
    description: String(o.description ?? ""),
  };
}

/** Normalize `GET /manual-assessment/process-templates` payload items. */
export function normalizeProcessTemplateGroups(data: unknown): ProcessTemplateGroup[] {
  if (!Array.isArray(data)) return [];
  const out: ProcessTemplateGroup[] = [];
  for (const item of data) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const process_template = normalizeProcessTemplateInfo(row.process_template);
    if (!process_template) continue;
    const subsRaw = row.sub_process_templates;
    const sub_process_templates = Array.isArray(subsRaw)
      ? subsRaw
          .map(normalizeProcessTemplateInfo)
          .filter((s): s is ProcessTemplateInfo => s != null)
      : [];
    out.push({ process_template, sub_process_templates });
  }
  return out;
}

/** Prefer catalog subprocess list for a main process (full template tree from API). */
export function subProcessesForMainFromCatalog(
  mainProcessId: number,
  catalog: readonly ProcessTemplateGroup[],
  fallback: readonly ManualProcessSummary[] = [],
): ManualProcessSummary[] {
  const group = catalog.find((g) => g.process_template.id === mainProcessId);
  const fromCatalog = group?.sub_process_templates ?? [];
  return fromCatalog.length > 0 ? [...fromCatalog] : [...fallback];
}

/** Auto-select first main that has subprocesses, else the only main, else null. */
export function pickDefaultMainProcessId(
  groups: readonly ProcessTemplateGroup[],
): number | null {
  const withSubs = groups.find((g) => g.sub_process_templates.length > 0);
  if (withSubs) return withSubs.process_template.id;
  if (groups.length === 1) return groups[0].process_template.id;
  return null;
}

/**
 * GET /manual-assessment/process-templates — Path B modal (no request params).
 */
export async function getManualAssessmentProcessTemplates(): Promise<
  ProcessTemplateGroup[]
> {
  const result = await apiClient.get<ProcessTemplateGroup[]>(
    "/manual-assessment/process-templates",
  );
  return normalizeProcessTemplateGroups(result.data);
}

/** Payload from GET /manual-assessment/process-definitions/{process_id} (data envelope). */
export type ManualProcessDefinitionPayload = {
  id?: string | number;
  processTemplateName?: string;
  process_template_name?: string;
  definitionXml?: string;
  definition_xml?: string;
};

export type ManualProcessDefinition = {
  processTemplateName: string | null;
  definitionXml: string;
};

function normalizeManualProcessDefinition(
  raw: ManualProcessDefinitionPayload | null | undefined,
): ManualProcessDefinition | null {
  if (!raw || typeof raw !== "object") return null;
  const xml = (
    typeof raw.definitionXml === "string"
      ? raw.definitionXml
      : typeof raw.definition_xml === "string"
        ? raw.definition_xml
        : ""
  ).trim();
  if (!xml) return null;
  const name =
    (typeof raw.processTemplateName === "string"
      ? raw.processTemplateName
      : typeof raw.process_template_name === "string"
        ? raw.process_template_name
        : ""
    ).trim() || null;
  return { processTemplateName: name, definitionXml: xml };
}

/**
 * GET /manual-assessment/process-definitions/{process_id} — BPMN diagram for View action.
 */
export async function getManualAssessmentProcessDefinition(
  processId: number,
): Promise<ManualProcessDefinition | null> {
  const result = await apiClient.get<ManualProcessDefinitionPayload>(
    `/manual-assessment/process-definitions/${processId}`,
  );
  return normalizeManualProcessDefinition(result.data);
}

import type {
  PreInitTask,
  PreInitWorkflowField,
  ProcessPreInitResponse,
} from "../types/manualAssessmentWorkflow";

export type { ProcessPreInitResponse } from "../types/manualAssessmentWorkflow";

function normalizePreInitWorkflowFields(raw: unknown): PreInitWorkflowField[] {
  if (!Array.isArray(raw)) return [];
  const out: PreInitWorkflowField[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    out.push(item as PreInitWorkflowField);
  }
  return out;
}

/**
 * GET /manual-assessment/process-templates/{processDefinitionId}/pre-init
 */
function normalizeProcessPreInitResponse(raw: unknown): ProcessPreInitResponse {
  const envelope =
    raw && typeof raw === "object" && "data" in (raw as Record<string, unknown>)
      ? (raw as { data?: unknown }).data
      : raw;

  if (!envelope || typeof envelope !== "object") {
    return { requiresInit: false };
  }

  const row = envelope as Record<string, unknown>;
  const requiresInit =
    row.requiresInit === true ||
    row.requires_init === true ||
    row.requiresInit === "true" ||
    row.requires_init === "true";

  const initTasksRaw = row.initTasks ?? row.init_tasks;
  const initTasks = Array.isArray(initTasksRaw) ? initTasksRaw : undefined;
  const workflowFields = normalizePreInitWorkflowFields(
    row.workflowFields ?? row.workflow_fields,
  );

  return {
    requiresInit,
    initTasks: initTasks as PreInitTask[] | undefined,
    workflowFields: workflowFields.length > 0 ? workflowFields : undefined,
  };
}

export async function getManualAssessmentProcessPreInit(
  processDefinitionId: string,
): Promise<ProcessPreInitResponse> {
  const id = processDefinitionId.trim();

  const endpoint = `/manual-assessment/process-templates/${encodeURIComponent(id)}/pre-init`;

  console.log("[ManualAssessment PreInit] GET pre-init request", {
    processDefinitionId: id,
    endpoint,
  });

  const result = await apiClient.get<unknown>(endpoint);

  console.log("[ManualAssessment PreInit] GET pre-init raw response", {
    processDefinitionId: id,
    result,
  });

  const normalized = normalizeProcessPreInitResponse(result.data ?? result);

  console.log("[ManualAssessment PreInit] GET pre-init normalized", {
    processDefinitionId: id,
    requiresInit: normalized.requiresInit,
    initTasksCount: normalized.initTasks?.length ?? 0,
    initTaskDefinitionIds: (normalized.initTasks ?? []).map((task) =>
      typeof task === "object" && task && "taskDefinitionId" in task
        ? (task as { taskDefinitionId?: string }).taskDefinitionId
        : undefined,
    ),
  });

  return normalized;
}

/**
 * POST /manual-assessment/workflow — Start Manual Assessment Initiate (snake_case body).
 */
export async function startManualAssessmentWorkflow(
  body: StartWorkflowRequest,
): Promise<ApiResponse<unknown>> {
  const result = await apiClient.post<unknown>(
    "/manual-assessment/workflow",
    body,
  );
  return {
    status: (result.status as "success" | "error") || "success",
    code: result.code ?? 200,
    message: result.message,
    data: result.data,
  };
}
